<script>
import moment from 'moment'
import { api } from '../../../firebase'
import LoadingEventsTable from '../events/LoadingEventsTable'
import LoadingImageTemplatesTable from '../image-templates/LoadingImageTemplatesTable'
import LoadingTextTemplatesTable from '../text-templates/LoadingTextTemplatesTable'
import CampaignSettingsForm from './CampaignSettingsForm'
import AdSetSettingsForm from './AdSetSettingsForm'
import AdCreativeSettingsForm from './AdCreativeSettingsForm'
// import AdSettingsForm from './AdSettingsForm'
import CampaignReviewScreen from './CampaignReviewScreen'
import EventsFilter from '../events/EventsFilter'
import { computeRelativeDate, NOW, ONE_DAY_BEFORE_END } from '../utils/relativeDates'

export default {
  name: 'MobilizeV2CreateCampaign',
  components: {
    LoadingEventsTable,
    LoadingImageTemplatesTable,
    LoadingTextTemplatesTable,
    CampaignSettingsForm,
    AdSetSettingsForm,
    AdCreativeSettingsForm,
    // AdSettingsForm,
    CampaignReviewScreen,
    EventsFilter
  },
  computed: {
    screen1Complete() {
      return (
        !!this.campaignSettings.status &&
        !!this.campaignSettings.objective &&
        ((this.campaignSettings.budget.enabled &&
          !!this.campaignSettings.budget.bidStrategy &&
          !!this.campaignSettings.budget.type &&
          this.campaignSettings.budget.amount !== null &&
          this.campaignSettings.budget.amount > 0) ||
          (this.adSetSettings.budget.enabled &&
            !!this.adSetSettings.budget.bidStrategy &&
            !!this.adSetSettings.budget.type &&
            this.adSetSettings.budget.amount !== null &&
            this.adSetSettings.budget.amount > 0)) &&
        !!this.adSetSettings.billingEvent &&
        !!this.adSetSettings.targeting &&
        !!this.adSetSettings.pixel &&
        !!this.adSetSettings.start &&
        !!this.adSetSettings.end &&
        !!this.adCreativeSettings.authorizationCategory &&
        !!this.adCreativeSettings.callToAction
      )
    }
  },
  data() {
    const now = moment()
    const tomorrow = moment(now)
      .set('day', now.day() + 1)
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0)

    return {
      loadingState: {
        pending: false,
        data: [],
        error: null
      },
      showEventsFilterDialog: false,
      eventsFilter: {
        data: {
          title: null,
          eventTypes: [],
          city: null,
          state: null,
          date: tomorrow.format('YYYY-MM-DD'),
          sponsorOrg: null,
          virtual: 'All'
        },
        filters: [{ key: 'start_date', comparator: '>=', value: tomorrow.unix() }]
      },
      campaignSettings: {
        name: undefined,
        status: 'PAUSED',
        objective: 'CONVERSIONS',
        budget: {
          enabled: false,
          type: 'LIFETIME',
          amount: null,
          bidStrategy: 'LOWEST_COST_WITHOUT_CAP'
        },
        specialAdCategories: ['NONE']
      },
      adSetSettings: {
        billingEvent: 'IMPRESSIONS',
        targeting: 'NATIONAL',
        start: { selectedOption: NOW, customDate: null, customTime: null },
        end: { selectedOption: ONE_DAY_BEFORE_END, customDate: null, customTime: null },
        budget: {
          enabled: true,
          type: 'LIFETIME',
          amount: 10000,
          bidStrategy: 'LOWEST_COST_WITHOUT_CAP'
        },
        pixel: null
      },
      adCreativeSettings: {
        authorizationCategory: 'NONE',
        callToAction: 'LEARN_MORE',
        urlTags: '',
        useEventImage: false
      },
      adSettings: {},
      events: [],
      imageTemplates: [],
      textTemplates: [],
      reviewScreenData: {},
      submitState: {
        pending: false,
        done: false,
        error: null,
        tasks: []
      },
      step: 1
    }
  },
  methods: {
    async applyFilter(filters) {
      this.eventsFilter.filters = filters
      this.showEventsFilterDialog = false
    },
    async submitForm() {
      this.submitState.pending = true
      this.submitState.done = false
      this.submitState.error = null
      this.$store.dispatch('startSpinner')
      const accessToken = this.$store.getters.getCurrentUser.token
      try {
        this.submitState.tasks = []
        const campaignCreationTask = {
          text: 'Creating campaign',
          pending: true,
          done: false,
          error: null
        }
        this.submitState.tasks.push(campaignCreationTask)
        const { id: campaignId } = await api.campaigns.create({
          accessToken,
          name: this.campaignSettings.name || undefined,
          events: this.events.map(event => event.id),
          textTemplates: this.textTemplates.map(textTemplate => textTemplate.id),
          imageTemplates: this.imageTemplates.map(imageTemplate => imageTemplate.id),
          status: this.campaignSettings.status,
          objective: this.campaignSettings.objective,
          bidStrategy: this.campaignSettings.bidStrategy,
          lifeTimeBudget: this.campaignSettings.lifeTimeBudget,
          specialAdCategories: this.campaignSettings.specialAdCategories
        })
        campaignCreationTask.pending = false
        campaignCreationTask.done = true

        let adSetCount = 0
        for (const adSet of this.reviewScreenData.adSets) {
          adSetCount = adSetCount + 1
          const adSetCreationTask = {
            text: `Creating ad set ${adSetCount} for event ${adSet.event.id}`,
            pending: true,
            done: false,
            error: null
          }
          this.submitState.tasks.push(adSetCreationTask)

          const timeslots = adSet.event.data.timeslots
          const eventStart = timeslots[0].start_date * 1000
          const eventEnd = timeslots[timeslots.length - 1].end_date * 1000
          const start =
            adSet.settings.start.selectedOption === 'CUSTOM'
              ? adSet.settings.start.customDate
              : computeRelativeDate(eventStart, eventEnd, adSet.settings.start.selectedOption)
          const end =
            adSet.settings.end.selectedOption === 'CUSTOM'
              ? adSet.settings.end.customDate
              : computeRelativeDate(eventStart, eventEnd, adSet.settings.end.selectedOption)

          const { id: adSetId } = await api.adSets.create({
            accessToken,
            campaign: campaignId,
            event: adSet.event.id,
            start,
            end,
            billingEvent: adSet.settings.billingEvent,
            targeting: adSet.settings.targeting,
            pixel: adSet.settings.pixel,
            budgetType: adSet.settings.budgetType,
            budget: adSet.settings.budget,
            bidStrategy: adSet.settings.bidStrategy
          })

          adSetCreationTask.done = true
          adSetCreationTask.pending = false

          let adCount = 0
          for (const ad of adSet.ads) {
            adCount = adCount + 1
            if (ad.enabled) {
              const adCreativeCreationTask = {
                text: `Creating creative for ad ${adCount} in ad set ${adSetCount}`,
                pending: true,
                done: false,
                error: null
              }
              this.submitState.tasks.push(adCreativeCreationTask)

              const { id: adCreativeId } = await api.adCreatives.create({
                accessToken,
                campaign: campaignId,
                event: adSet.event.id,
                textTemplate: ad.textTemplate.id,
                imageTemplate: ad.creative.settings.useEventImage ? undefined : ad.imageTemplate.id,
                authorizationCategory: ad.creative.settings.authorizationCategory,
                urlTags: ad.creative.settings.urlTags,
                callToAction: ad.creative.settings.callToAction,
                adSet: adSetId
              })

              adCreativeCreationTask.done = true
              adCreativeCreationTask.pending = false

              const adCreationTask = {
                text: `Creating ad ${adCount} in ad set ${adSetCount}`,
                pending: true,
                done: false,
                error: null
              }
              this.submitState.tasks.push(adCreationTask)

              await api.ads.create({
                accessToken,
                campaign: campaignId,
                adSet: adSetId,
                adCreative: adCreativeId,
                event: adSet.event.id,
                textTemplate: ad.textTemplate.id,
                imageTemplate: ad.creative.settings.useEventImage ? undefined : ad.imageTemplate.id,
                pixel: adSet.settings.pixel,
                callToAction: ad.creative.settings.callToAction
              })

              adCreationTask.done = true
              adCreationTask.pending = false
            } else {
              this.submitState.tasks.push({
                text: `Skipping ad ${adCount} in ad set ${adSetCount} because it is not enabled`,
                pending: false,
                done: true,
                error: null
              })
            }
          }
        }
        this.submitState.done = true
        this.$toast.success('Campaign created successfully')
        return { id: campaignId }
      } catch (error) {
        this.submitState.error = error
        this.submitState.tasks[this.submitState.tasks.length - 1].error = error
        this.submitState.tasks[this.submitState.tasks.length - 1].pending = false
        this.$toast.error('Campaign creation failed')
      } finally {
        this.submitState.pending = false
        this.$store.dispatch('stopSpinner')
      }
    },
    async onCreateButtonClick() {
      this.step = this.step + 1
      const response = await this.submitForm()
      if (this.submitState.error) {
        return
      }
      const { id } = response
      this.$router.push(`/mobilizev2/campaigns/${id}`)
    }
  },
  watch: {
    campaignSettings: {
      deep: true,
      handler: function(oldValue, newValue) {
        this.adSetSettings.budget.enabled = !newValue.budget.enabled
      }
    },
    adSetSettings: {
      deep: true,
      handler: function(oldValue, newValue) {
        this.campaignSettings.budget.enabled = !newValue.budget.enabled
      }
    }
  }
}
</script>

<template>
  <div class="mobilizev2-view">
    <h1>Create a Campaign</h1>
    <v-form>
      <v-stepper v-model="step">
        <!-- Stepper header -->
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" :step="1">
            Campaign &amp; Ad Settings
          </v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="step > 2" :step="2">
            Events
          </v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="step > 3" :step="3">
            Text Templates
          </v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="step > 4" :step="4">
            Image Templates
          </v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="step > 5" :step="5">
            Review
          </v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="submitState.done" :step="6">
            Create
          </v-stepper-step>
        </v-stepper-header>
        <!-- Stepper items -->
        <v-stepper-items>
          <!-- Campaign & Ad Settings -->
          <v-stepper-content :step="1">
            <h2 class="my-4">Campaign &amp; Ad Settings</h2>
            <h3 class="my-4">Campaign Settings</h3>
            <div class="my-4">
              <campaign-settings-form v-model="campaignSettings" />
            </div>
            <h3 class="my-4">Ad Set Settings</h3>
            <div class="my-4">
              <ad-set-settings-form v-model="adSetSettings" />
            </div>
            <h3 class="my-4">Ad Settings</h3>
            <div class="my-4">
              <ad-creative-settings-form v-model="adCreativeSettings" />
            </div>
            <!-- <h3 class="my-4">Ad Settings</h3>
            <div class="my-4">
              <ad-settings-form v-model="adSettings" />
            </div> -->
            <div class="d-flex flex-row-reverse">
              <v-btn class="mx-1 mr-0" color="primary" :disabled="!screen1Complete" @click="step = step + 1">
                Continue
              </v-btn>
              <v-btn text @click="$router.push('/mobilizev2/campaigns')">
                Back
              </v-btn>
            </div>
          </v-stepper-content>
          <!-- Events -->
          <v-stepper-content :step="2">
            <v-row>
              <v-col sm="auto" md="auto" lg="auto" xl="auto">
                <h2 class="my-4">Events</h2>
              </v-col>
              <v-spacer />
              <v-col class="filter-button" sm="auto" md="auto" lg="auto" xl="auto">
                <v-dialog
                  v-model="showEventsFilterDialog"
                  fullscreen
                  hide-overlay
                  transition="dialog-bottom-transition"
                  class="filter-dialogue"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" v-bind="attrs" v-on="on" :disabled="loadingState.pending">
                      <v-icon>filter_list</v-icon>
                      Filter events
                    </v-btn>
                  </template>
                  <events-filter
                    @close-filter="showEventsFilterDialog = false"
                    v-model="eventsFilter.data"
                    @apply-filter="applyFilter"
                  />
                </v-dialog>
              </v-col>
            </v-row>
            <div class="my-4">
              <loading-events-table
                :loadingState.sync="loadingState"
                :show-select="true"
                :filters="eventsFilter.filters"
                v-model="events"
              />
            </div>
            <div class="d-flex flex-row-reverse">
              <v-btn color="primary" :disabled="events.length === 0" @click="step = step + 1">
                Continue
              </v-btn>
              <v-btn text @click="step = step - 1">
                Back
              </v-btn>
            </div>
          </v-stepper-content>
          <!-- Text templates -->
          <v-stepper-content :step="3">
            <h2 class="my-4">Text templates</h2>
            <div class="my-4">
              <loading-text-templates-table :show-select="true" :showActions="false" v-model="textTemplates" />
            </div>
            <div class="d-flex flex-row-reverse">
              <v-btn class="mx-1 mr-0" color="primary" :disabled="textTemplates.length === 0" @click="step = step + 1">
                Continue
              </v-btn>
              <v-btn text @click="step = step - 1">
                Back
              </v-btn>
            </div>
          </v-stepper-content>
          <!-- Image templates -->
          <v-stepper-content :step="4">
            <h2 class="my-4">Image templates</h2>
            <div class="my-4">
              <loading-image-templates-table :show-select="true" :showActions="false" v-model="imageTemplates" />
            </div>
            <div class="d-flex flex-row-reverse">
              <v-btn class="mx-1 mr-0" color="primary" :disabled="imageTemplates.length === 0" @click="step = step + 1">
                Continue
              </v-btn>
              <v-btn text @click="step = step - 1">
                Back
              </v-btn>
            </div>
          </v-stepper-content>
          <!-- Review -->
          <v-stepper-content :step="5">
            <h2 class="my-4">Review</h2>
            <campaign-review-screen
              v-if="step === 5"
              :campaign-settings="campaignSettings"
              :ad-set-settings="adSetSettings"
              :ad-creative-settings="adCreativeSettings"
              :ad-settings="adSettings"
              :events="events"
              :text-templates="textTemplates"
              :image-templates="imageTemplates"
              @input="reviewScreenData = $event"
            />
            <div class="d-flex flex-row-reverse">
              <v-btn
                color="primary"
                class="mini-hidden mx-1 mr-0"
                :disabled="submitState.pending"
                @click="onCreateButtonClick"
              >
                <v-icon left dark>add</v-icon>
                Create
              </v-btn>
              <v-btn text @click="step = step - 1">
                Back
              </v-btn>
            </div>
          </v-stepper-content>
          <v-stepper-content :step="6">
            <h2 class="my-4">Creating your campaign...</h2>
            <ul>
              <li class="block" :key="index" v-for="(task, index) in submitState.tasks">
                {{ task.text }}: {{ task.pending ? 'Pending...' : task.done ? 'Done' : 'Errored' }}
              </li>
            </ul>
            <v-alert class="block" v-if="submitState.error">
              <p>{{ submitState.error.message }}</p>
              <v-btn text @click="step = step - 1">
                Try again
              </v-btn>
            </v-alert>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-form>
  </div>
</template>

<style lang="scss" scoped>
.block {
  display: block;
}

.v-application .my-4 {
  margin-top: 0 !important;
}

.filter-button {
  margin-right: 0;
  margin-bottom: 0;
}
</style>
